import { displayStringForCommentPostTime } from '@/utils/dateTime'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import { Avatar } from 'antd'
import { JEPNotificationType } from 'common/src/api/websiteFrontendVsWebsiteBackend/notifications/types'
import React, { ReactElement, useMemo } from 'react'
import {
    CommentOutlined,
    SoundOutlined,
    BulbOutlined,
    MessageOutlined,
} from '@ant-design/icons'
import {
    NotificationListItemProps,
    invalidateContentCommentThread,
    prettifyContentType,
    buildHrefForCommentContentTypeAndId,
    buildHrefForMnemonicSuggestion,
} from '@/components/NotificationModal/NotificationListItem/NotificationListItem.types'
import Link from 'next/link'
import styles from './NotificationListItem.module.less'
import { UrlObject } from 'url'
import { UserType } from 'magic-text-box-lexical/src/context/AnnotatedContentContext/AnnotatedContent.types'
import { useSetRecoilState } from 'recoil'
import {
    selectedFeedbackNotificationAtom,
    pendingMnemonicSuggestionAtom,
} from '@/atoms/notifications'

export const NotificationListItem: React.FC<NotificationListItemProps> = (
    props
): ReactElement => {
    const { notification } = props
    const setSelectedFeedback = useSetRecoilState(
        selectedFeedbackNotificationAtom
    )
    const setPendingMnemonicSuggestion = useSetRecoilState(
        pendingMnemonicSuggestionAtom
    )
    const { avatar, href, textToShow } = useMemo((): {
        avatar: ReactElement
        href: string | UrlObject | null
        textToShow: ReactElement
    } => {
        switch (notification.type) {
            case JEPNotificationType.COMMENT:
                return {
                    avatar: (
                        <Avatar
                            className={`${styles.avatar} ${
                                notification.metadata.commentUserType ===
                                UserType.instructor
                                    ? styles.avatarInstructor
                                    : styles.avatarStudent
                            }`}
                            size="large"
                            icon={<CommentOutlined />}
                        />
                    ),
                    href: buildHrefForCommentContentTypeAndId(
                        notification.metadata.contentType,
                        notification.metadata.contentPath,
                        notification.metadata.contentId
                    ),
                    textToShow: (
                        <div>
                            <b>
                                {notification.metadata.commentUserDisplayName}
                            </b>{' '}
                            has commented on{' '}
                            {prettifyContentType(
                                notification.metadata.contentType
                            )}
                            .
                        </div>
                    ),
                }
            case JEPNotificationType.ANNOUNCEMENT:
                return {
                    avatar: (
                        <Avatar
                            size={'large'}
                            className={'avatar avatar-announcement'}
                            icon={<SoundOutlined />}
                        />
                    ),
                    href: notification.metadata.externalHref,
                    textToShow: (
                        <div>
                            <b>Announcement: </b>
                            {notification.metadata.text}
                        </div>
                    ),
                }
            case JEPNotificationType.MNEMONIC_SUGGESTION:
                return {
                    avatar: (
                        <Avatar
                            size={'large'}
                            className={`${styles.avatar} ${styles.avatarFeature}`}
                            icon={<BulbOutlined />}
                        />
                    ),
                    href: buildHrefForMnemonicSuggestion(notification.metadata),
                    textToShow: (
                        <div>
                            <b>Mnemonic suggestion</b> from{' '}
                            {notification.metadata.userEmail}: &ldquo;
                            {notification.metadata.proposedMnemonic}&rdquo;
                        </div>
                    ),
                }
            case JEPNotificationType.FEEDBACK:
                return {
                    avatar: (
                        <Avatar
                            size={'large'}
                            className={`${styles.avatar} ${styles.avatarAnnouncement}`}
                            icon={<MessageOutlined />}
                        />
                    ),
                    href: null,
                    textToShow: (
                        <div>
                            <b>Feedback</b> from{' '}
                            {notification.metadata.userEmail}: &ldquo;
                            {notification.metadata.feedback}&rdquo;
                        </div>
                    ),
                }
        }
    }, [notification])

    const listItemContent = (
        <div className={styles.notificationListItemContainer}>
            <div className={styles.notificationListItemAvatar}>{avatar}</div>
            <div
                className={`${styles.notificationListItemTextContainer} ${reusableCssClass.centerChildrenVertically}`}
            >
                <div className={styles.notificationListItemTextInfo}>
                    {textToShow}
                </div>
                <div className={styles.notificationListItemTextTime}>
                    {displayStringForCommentPostTime(notification.createdAt)}
                </div>
            </div>
            <div
                className={`${styles.notificationListItemDot} ${
                    notification.hasBeenSeen ? styles.hidden : ''
                }`}
            >
                &#9679;
            </div>
        </div>
    )

    if (href === null) {
        return (
            <div
                className={reusableCssClass.clickMe}
                onClick={() => setSelectedFeedback(notification)}
            >
                {listItemContent}
            </div>
        )
    }

    return (
        <Link
            href={href}
            className={reusableCssClass.clickMe}
            onClick={() => {
                if (notification.type === JEPNotificationType.COMMENT) {
                    invalidateContentCommentThread(notification.metadata)
                }
                // The href lands on the notecard; hand the suggestion to
                // the review panel that renders inside its editor.
                if (
                    notification.type ===
                    JEPNotificationType.MNEMONIC_SUGGESTION
                ) {
                    setPendingMnemonicSuggestion(notification)
                }
                // Don't programmatically close the modal here.
                // The Link navigation changes the URL, which triggers
                // useModalUrlSync's URL→atoms effect to close the modal.
                // Closing it here would cause useModalUrlSync's atoms→URL
                // effect to router.push to the current page, overriding
                // the Link's navigation.
            }}
        >
            {listItemContent}
        </Link>
    )
}
